/**
 * PromoBanner component for the Indigo design system.
 *
 * A large split hero / empty-state card: content on the left (Society-font
 * title, description, action buttons) and a tinted illustration panel on the
 * right. Used to introduce a feature before there's any data to show.
 *
 * The illustration is caller-supplied (design-system components don't own app
 * assets) — pass any node, typically an `<img>`.
 *
 * @example
 * ```tsx
 * <PromoBanner
 *   title="Refer a physician, earn 10% payout"
 *   description={<>Welcome, Dr. Gordon! …</>}
 *   actions={<Button>Contact us</Button>}
 *   illustration={<img src={singleDoctorImage} alt="" />}
 *   accentColor="amber"
 * />
 * ```
 */
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { forwardRef, type ReactNode } from "react";
import { Heading } from "../Typography/Heading";
import "./PromoBanner.css";

const accentPanelVariants = cva("indigo-promo-banner__panel", {
  variants: {
    accentColor: {
      amber: "indigo-promo-banner__panel--amber",
      olive: "indigo-promo-banner__panel--olive",
      sky: "indigo-promo-banner__panel--sky",
      rose: "indigo-promo-banner__panel--rose",
    },
  },
  defaultVariants: {
    accentColor: "amber",
  },
});

export interface PromoBannerProps
  extends VariantProps<typeof accentPanelVariants> {
  /**
   * Headline, rendered as a large Society-font heading.
   */
  title: ReactNode;

  /**
   * Supporting copy below the title. Rendered in the tertiary text color.
   */
  description?: ReactNode;

  /**
   * Action buttons rendered below the description.
   */
  actions?: ReactNode;

  /**
   * Illustration shown in the accent panel (typically an `<img>`). When
   * omitted the panel still renders as a colored block.
   */
  illustration?: ReactNode;

  /**
   * Accent color for the illustration panel.
   * @default "amber"
   */
  accentColor?: "amber" | "olive" | "sky" | "rose";

  /**
   * Additional CSS classes applied to the banner container.
   */
  className?: string;
}

/**
 * PromoBanner component
 *
 * Renders a two-column hero card. Collapses to a single column on small screens.
 */
export const PromoBanner = forwardRef<HTMLDivElement, PromoBannerProps>(
  (
    {
      title,
      description,
      actions,
      illustration,
      accentColor = "amber",
      className,
    },
    ref,
  ) => {
    return (
      <div ref={ref} className={clsx("indigo-promo-banner", className)}>
        <div className="indigo-promo-banner__content">
          {/* Title / text / actions block — vertically centered (auto
              top/bottom margins), horizontally centered, capped width. */}
          <div className="indigo-promo-banner__inner">
            <Heading
              level={1}
              fontFamily="society"
              fontSizePx={40}
              className="indigo-promo-banner__title"
            >
              {title}
            </Heading>
            {description && (
              <div className="indigo-promo-banner__text">{description}</div>
            )}
            {actions && (
              <div className="indigo-promo-banner__actions">{actions}</div>
            )}
          </div>
        </div>
        <div className={accentPanelVariants({ accentColor })}>
          {illustration && (
            <div
              className="indigo-promo-banner__illustration"
              aria-hidden="true"
            >
              {illustration}
            </div>
          )}
        </div>
      </div>
    );
  },
);

PromoBanner.displayName = "PromoBanner";

export { accentPanelVariants };
